import React, { FC, useState, useEffect } from "react";
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  notification, 
  Modal, 
  Select,
  Typography,
  Divider,
  Statistic,
  Row,
  Col,
  Form,
  InputNumber,
  Input
} from "antd";
import { 
  HomeOutlined,
  ReloadOutlined,
  PlayCircleOutlined,
  QuestionCircleOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  EditOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useGetScoringQuestions } from '../utils/hooks';
import { populateScoringQuestions, getAuthToken, updateScoringQuestion, getCategoryWeightages, updateCategoryWeightage, getLikelihoodScores, updateLikelihoodScore } from '../utils/functions';
import { CaseScoringQuestionsUrl, CategoryWeightagesUrl, LikelihoodScoresUrl } from '../utils/network';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

interface ScoringQuestion {
  id: number;
  category: string;
  category_display: string;
  basis: string | null;
  case_type: string | null;
  question: string;
  answer: string;
  score: number;
  description: string | null;
  order: number;
}

const ScoringMethod: FC = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<ScoringQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [filters, setFilters] = useState<{ category?: string; case_type?: string; basis?: string }>({});
  const [populateModalVisible, setPopulateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<ScoringQuestion | null>(null);
  const [form] = Form.useForm();
  const [updating, setUpdating] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [algorithmModalVisible, setAlgorithmModalVisible] = useState(false);
  const [weightages, setWeightages] = useState<any[]>([]);
  const [likelihoodScores, setLikelihoodScores] = useState<any[]>([]);
  const [loadingWeightages, setLoadingWeightages] = useState(false);
  const [loadingLikelihood, setLoadingLikelihood] = useState(false);
  const [updatingWeightage, setUpdatingWeightage] = useState<number | null>(null);
  const [updatingLikelihood, setUpdatingLikelihood] = useState<number | null>(null);
  const [weightageForm] = Form.useForm();
  const [likelihoodForm] = Form.useForm();

  // Restrict access to admin users only
  useEffect(() => {
    const role = localStorage.getItem('token1');
    if (!role || role.toLowerCase() !== 'admin') {
      navigate('/');
    }
  }, [navigate]);

  // Use hook to fetch scoring questions
  useGetScoringQuestions(setQuestions, setLoading, filters);

  const handlePopulate = async (overwrite: boolean = false) => {
    setPopulating(true);
    try {
      const result = await populateScoringQuestions(overwrite);
      notification.success({
        message: 'Success',
        description: `Scoring questions populated successfully! Created: ${result.created}, Skipped: ${result.skipped}`,
        duration: 5,
      });
      setPopulateModalVisible(false);
      // Refresh the list
      setLoading(true);
      const headers = getAuthToken();
      if (headers) {
        const response = await axios.get(CaseScoringQuestionsUrl, headers);
        setQuestions(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to populate scoring questions. Please try again.',
      });
    } finally {
      setPopulating(false);
    }
  };

  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  const handleEdit = (record: ScoringQuestion) => {
    setEditingQuestion(record);
    form.setFieldsValue({
      score: record.score,
      description: record.description || '',
      answer: record.answer,
      order: record.order
    });
    setEditModalVisible(true);
  };

  const handleUpdate = async () => {
    if (!editingQuestion) return;
    
    try {
      const values = await form.validateFields();
      setUpdating(true);
      
      await updateScoringQuestion(editingQuestion.id, {
        score: values.score,
        description: values.description,
        answer: values.answer,
        order: values.order
      });
      
      notification.success({
        message: 'Success',
        description: 'Scoring question updated successfully!',
      });
      
      setEditModalVisible(false);
      setEditingQuestion(null);
      form.resetFields();
      
      // Refresh the list
      setLoading(true);
      const headers = getAuthToken();
      if (headers) {
        const response = await axios.get(CaseScoringQuestionsUrl, headers);
        setQuestions(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      if (error.errorFields) {
        // Form validation error
        return;
      }
      notification.error({
        message: 'Error',
        description: error.response?.data?.message || 'Failed to update scoring question. Please try again.',
      });
    } finally {
      setUpdating(false);
    }
  };

  const columns = [
    {
      title: 'Question',
      dataIndex: 'question',
      key: 'question',
      width: '30%',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Answer',
      dataIndex: 'answer',
      key: 'answer',
      width: '25%',
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      width: '10%',
      render: (score: number) => (
        <Tag color={score >= 8 ? 'red' : score >= 5 ? 'orange' : 'green'}>
          {score}
        </Tag>
      ),
      sorter: (a: ScoringQuestion, b: ScoringQuestion) => a.score - b.score,
    },
    {
      title: 'Category',
      dataIndex: 'category_display',
      key: 'category',
      width: '15%',
      render: (text: string) => <Tag>{text}</Tag>
    },
    {
      title: 'Case Type',
      dataIndex: 'case_type',
      key: 'case_type',
      width: '15%',
      render: (text: string | null) => text ? <Tag color="blue">{text}</Tag> : <Tag>All</Tag>
    },
    {
      title: 'Basis',
      dataIndex: 'basis',
      key: 'basis',
      width: '12%',
      render: (text: string | null) => text ? <Tag color="purple">{text}</Tag> : <Tag>All</Tag>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: '8%',
      render: (_: any, record: ScoringQuestion) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
          size="small"
        >
          Edit
        </Button>
      ),
    },
  ];

  const totalQuestions = new Set(questions.map(q => q.question)).size;
  const totalAnswers = questions.length;
  const avgScore = questions.length > 0 
    ? (questions.reduce((sum, q) => sum + q.score, 0) / questions.length).toFixed(2)
    : 0;

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <Card style={{ marginBottom: '24px' }}>
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <div>
            <Title level={2} style={{ margin: 0 }}>
              <QuestionCircleOutlined /> Scoring Method
            </Title>
            <Text type="secondary">
              Manage case scoring questions and answers with default scores
            </Text>
          </div>
          <Space>
            <Button
              icon={<HomeOutlined />}
              onClick={() => navigate('/')}
            >
              Home
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={async () => {
                setLoading(true);
                const headers = getAuthToken();
                if (headers) {
                  try {
                    const response = await axios.get(CaseScoringQuestionsUrl, headers);
                    setQuestions(response.data);
                  } catch (error) {
                    console.error('Error refreshing:', error);
                  }
                }
                setLoading(false);
              }}
              loading={loading}
            >
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => setPopulateModalVisible(true)}
            >
              Populate Default Scores
            </Button>
            <Button
              type="default"
              icon={<InfoCircleOutlined />}
              onClick={() => {
                setAlgorithmModalVisible(true);
                // Fetch weightages and likelihood scores
                getCategoryWeightages(setWeightages, setLoadingWeightages);
                getLikelihoodScores(setLikelihoodScores, setLoadingLikelihood);
              }}
            >
              Algorithm Settings
            </Button>
          </Space>
        </Space>
      </Card>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Questions"
              value={totalQuestions}
              prefix={<QuestionCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Answer Options"
              value={totalAnswers}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Average Score"
              value={avgScore}
              prefix={<InfoCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card style={{ marginBottom: '24px' }}>
        <Space wrap>
          <Text strong>Filters:</Text>
          <Select
            placeholder="Category"
            style={{ width: 150 }}
            allowClear
            value={filters.category}
            onChange={(value) => handleFilterChange('category', value)}
          >
            <Option value="B">Behavior</Option>
            <Option value="S">Situation</Option>
            <Option value="D">Duration</Option>
            <Option value="R">Result/Impact</Option>
            <Option value="W">Weight/Severity</Option>
            <Option value="F">Frequency</Option>
          </Select>
          <Select
            placeholder="Case Type"
            style={{ width: 180 }}
            allowClear
            value={filters.case_type}
            onChange={(value) => handleFilterChange('case_type', value)}
          >
            <Option value="Accommodation">Accommodation</Option>
            <Option value="Different Treatment">Different Treatment</Option>
            <Option value="Harassment">Harassment</Option>
            <Option value="Nonselection">Nonselection</Option>
            <Option value="Sexual Harassment">Sexual Harassment</Option>
            <Option value="Termination">Termination</Option>
          </Select>
          <Select
            placeholder="Basis"
            style={{ width: 200 }}
            allowClear
            value={filters.basis}
            onChange={(value) => handleFilterChange('basis', value)}
          >
            <Option value="Age">Age</Option>
            <Option value="Color">Color</Option>
            <Option value="Mental/Physical Handicap">Mental/Physical Handicap</Option>
            <Option value="National Origin">National Origin</Option>
            <Option value="Race">Race</Option>
            <Option value="Religion">Religion</Option>
            <Option value="Reprisal">Reprisal</Option>
            <Option value="Sex">Sex</Option>
          </Select>
          <Button onClick={clearFilters}>Clear Filters</Button>
        </Space>
      </Card>

      {/* Questions Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={questions}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} answer options`,
            onShowSizeChange: (current, size) => {
              setPageSize(size);
            }
          }}
          components={{
            body: {
              cell: (props: any) => (
                <td {...props} style={{ ...props.style, padding: '4px 8px', lineHeight: '1.2' }} />
              ),
              row: (props: any) => (
                <tr {...props} style={{ ...props.style, lineHeight: '1.2' }} />
              ),
            },
          }}
          expandable={{
            expandedRowRender: (record: ScoringQuestion) => (
              <div style={{ padding: '8px 12px', background: '#f5f5f5', borderRadius: '4px', lineHeight: '1.2' }}>
                <Text strong>Description: </Text>
                <Text>{record.description || 'No description'}</Text>
              </div>
            ),
          }}
        />
      </Card>

      {/* Populate Modal */}
      <Modal
        title="Populate Default Scoring Questions"
        open={populateModalVisible}
        onOk={() => handlePopulate(false)}
        onCancel={() => setPopulateModalVisible(false)}
        okText="Populate (Skip Existing)"
        cancelText="Cancel"
        confirmLoading={populating}
        footer={[
          <Button key="cancel" onClick={() => setPopulateModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="overwrite" danger onClick={() => handlePopulate(true)} loading={populating}>
            Overwrite All
          </Button>,
          <Button key="populate" type="primary" onClick={() => handlePopulate(false)} loading={populating}>
            Populate (Skip Existing)
          </Button>,
        ]}
      >
        <div>
          <p>This will populate the database with default scoring questions and answers based on your form fields.</p>
          <Divider />
          <Text strong>Options:</Text>
          <ul>
            <li><Text strong>Populate (Skip Existing):</Text> Creates new records but skips any that already exist</li>
            <li><Text strong>Overwrite All:</Text> Deletes all existing records and creates fresh ones</li>
          </ul>
          <Divider />
          <Text type="secondary">
            This will create scoring questions for all case types and bases with predefined scores based on the scoring method reference table.
          </Text>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title="Edit Scoring Question"
        open={editModalVisible}
        onOk={handleUpdate}
        onCancel={() => {
          setEditModalVisible(false);
          setEditingQuestion(null);
          form.resetFields();
        }}
        okText="Update"
        cancelText="Cancel"
        confirmLoading={updating}
        style={{ top: 20 }}
        width={500}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto' }}
      >
        {editingQuestion && (
          <Form
            form={form}
            layout="vertical"
            initialValues={{
              score: editingQuestion.score,
              description: editingQuestion.description || '',
              answer: editingQuestion.answer,
              order: editingQuestion.order
            }}
          >
            <Form.Item label="Question" style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: '12px' }}>{editingQuestion.question}</Text>
            </Form.Item>
            <Form.Item label="Category" style={{ marginBottom: 12 }}>
              <Tag>{editingQuestion.category_display}</Tag>
            </Form.Item>
            <Divider style={{ margin: '8px 0' }} />
            <Form.Item
              label="Answer"
              name="answer"
              rules={[{ required: true, message: 'Please enter the answer' }]}
              style={{ marginBottom: 16 }}
            >
              <Input placeholder="Answer text" />
            </Form.Item>
            <Form.Item
              label="Score"
              name="score"
              rules={[
                { required: true, message: 'Please enter the score' },
                { type: 'number', min: 0, max: 10, message: 'Score must be between 0 and 10' }
              ]}
              style={{ marginBottom: 16 }}
            >
              <InputNumber
                min={0}
                max={10}
                style={{ width: '100%' }}
                placeholder="Enter score (0-10)"
              />
            </Form.Item>
            <Form.Item
              label="Description"
              name="description"
              style={{ marginBottom: 16 }}
            >
              <Input.TextArea
                rows={2}
                placeholder="Optional description"
              />
            </Form.Item>
            <Form.Item
              label="Order"
              name="order"
              rules={[
                { required: true, message: 'Please enter the order' },
                { type: 'number', min: 1, message: 'Order must be at least 1' }
              ]}
              style={{ marginBottom: 8 }}
            >
              <InputNumber
                min={1}
                style={{ width: '100%' }}
                placeholder="Display order"
              />
            </Form.Item>
          </Form>
        )}
      </Modal>

      {/* Algorithm Settings Modal */}
      <Modal
        title={<span style={{ fontSize: '16px' }}>Algorithm Settings</span>}
        open={algorithmModalVisible}
        onCancel={() => {
          setAlgorithmModalVisible(false);
          weightageForm.resetFields();
          likelihoodForm.resetFields();
        }}
        footer={null}
        width={700}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: '80vh', overflowY: 'auto', padding: '16px' }}
      >
        <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: '13px' }}>Category Weightages</Text>
          <Button
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={async () => {
              try {
                const headers = getAuthToken();
                if (headers) {
                  const response = await axios.post(
                    `${CategoryWeightagesUrl}populate_default/`,
                    { overwrite: false },
                    headers
                  );
                  notification.success({
                    message: 'Success',
                    description: response.data.message,
                  });
                  getCategoryWeightages(setWeightages, setLoadingWeightages);
                }
              } catch (error: any) {
                notification.error({
                  message: 'Error',
                  description: error.response?.data?.message || 'Failed to populate default weightages',
                });
              }
            }}
          >
            Defaults
          </Button>
        </div>
        
        <Table
          dataSource={weightages}
          rowKey="id"
          loading={loadingWeightages}
          pagination={false}
          size="small"
          components={{
            body: {
              cell: (props: any) => (
                <td {...props} style={{ ...props.style, padding: '6px 8px', fontSize: '12px' }} />
              ),
              row: (props: any) => (
                <tr {...props} style={{ ...props.style, lineHeight: '1.3' }} />
              ),
            },
          }}
          columns={[
            {
              title: <span style={{ fontSize: '12px' }}>Category</span>,
              dataIndex: 'category_display',
              key: 'category',
              width: '35%',
            },
            {
              title: <span style={{ fontSize: '12px' }}>Weightage</span>,
              key: 'weightage',
              width: '50%',
              render: (_: any, record: any) => (
                <InputNumber
                  min={0}
                  max={1}
                  step={0.0001}
                  precision={4}
                  value={parseFloat(record.weightage)}
                  onChange={async (value) => {
                    if (value !== null && value !== undefined) {
                      setUpdatingWeightage(record.id);
                      try {
                        await updateCategoryWeightage(record.id, value);
                        notification.success({
                          message: 'Success',
                          description: `Weightage updated for ${record.category_display}`,
                        });
                        getCategoryWeightages(setWeightages, setLoadingWeightages);
                      } catch (error) {
                        notification.error({
                          message: 'Error',
                          description: 'Failed to update weightage',
                        });
                      } finally {
                        setUpdatingWeightage(null);
                      }
                    }
                  }}
                  disabled={updatingWeightage === record.id}
                  style={{ width: '100%', fontSize: '12px' }}
                  size="small"
                />
              ),
            },
            {
              title: <span style={{ fontSize: '12px' }}>Status</span>,
              key: 'status',
              width: '15%',
              render: (_: any, record: any) => (
                updatingWeightage === record.id ? (
                  <Tag color="processing" style={{ fontSize: '11px', padding: '2px 6px', margin: 0 }}>Updating</Tag>
                ) : (
                  <Tag color="success" style={{ fontSize: '11px', padding: '2px 6px', margin: 0 }}>Saved</Tag>
                )
              ),
            },
          ]}
        />

        <Divider style={{ margin: '16px 0' }} />

        <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: '13px' }}>Likelihood Score Ranges</Text>
          <Button
            size="small"
            icon={<PlayCircleOutlined />}
            onClick={async () => {
              try {
                const headers = getAuthToken();
                if (headers) {
                  const response = await axios.post(
                    `${LikelihoodScoresUrl}populate_default/`,
                    { overwrite: false },
                    headers
                  );
                  notification.success({
                    message: 'Success',
                    description: response.data.message,
                  });
                  getLikelihoodScores(setLikelihoodScores, setLoadingLikelihood);
                }
              } catch (error: any) {
                notification.error({
                  message: 'Error',
                  description: error.response?.data?.message || 'Failed to populate default likelihood scores',
                });
              }
            }}
          >
            Defaults
          </Button>
        </div>

        <Table
          dataSource={likelihoodScores}
          rowKey="id"
          loading={loadingLikelihood}
          pagination={false}
          size="small"
          components={{
            body: {
              cell: (props: any) => (
                <td {...props} style={{ ...props.style, padding: '6px 8px', fontSize: '12px' }} />
              ),
              row: (props: any) => (
                <tr {...props} style={{ ...props.style, lineHeight: '1.3' }} />
              ),
            },
          }}
          columns={[
            {
              title: <span style={{ fontSize: '12px' }}>Class</span>,
              dataIndex: 'class_name',
              key: 'class_name',
              width: '20%',
              render: (text: string) => (
                <Tag 
                  color={text === 'Low' ? 'green' : text === 'Medium' ? 'orange' : 'red'}
                  style={{ fontSize: '11px', padding: '2px 6px', margin: 0 }}
                >
                  {text}
                </Tag>
              ),
            },
            {
              title: <span style={{ fontSize: '12px' }}>Min Range</span>,
              key: 'min_range',
              width: '35%',
              render: (_: any, record: any) => (
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  value={parseFloat(record.min_range)}
                  onChange={async (value) => {
                    if (value !== null && value !== undefined) {
                      setUpdatingLikelihood(record.id);
                      try {
                        await updateLikelihoodScore(record.id, { min_range: value });
                        notification.success({
                          message: 'Success',
                          description: `Min range updated for ${record.class_name}`,
                        });
                        getLikelihoodScores(setLikelihoodScores, setLoadingLikelihood);
                      } catch (error) {
                        notification.error({
                          message: 'Error',
                          description: 'Failed to update min range',
                        });
                      } finally {
                        setUpdatingLikelihood(null);
                      }
                    }
                  }}
                  disabled={updatingLikelihood === record.id}
                  style={{ width: '100%', fontSize: '12px' }}
                  size="small"
                />
              ),
            },
            {
              title: <span style={{ fontSize: '12px' }}>Max Range</span>,
              key: 'max_range',
              width: '35%',
              render: (_: any, record: any) => (
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  value={parseFloat(record.max_range)}
                  onChange={async (value) => {
                    if (value !== null && value !== undefined) {
                      setUpdatingLikelihood(record.id);
                      try {
                        await updateLikelihoodScore(record.id, { max_range: value });
                        notification.success({
                          message: 'Success',
                          description: `Max range updated for ${record.class_name}`,
                        });
                        getLikelihoodScores(setLikelihoodScores, setLoadingLikelihood);
                      } catch (error) {
                        notification.error({
                          message: 'Error',
                          description: 'Failed to update max range',
                        });
                      } finally {
                        setUpdatingLikelihood(null);
                      }
                    }
                  }}
                  disabled={updatingLikelihood === record.id}
                  style={{ width: '100%', fontSize: '12px' }}
                  size="small"
                />
              ),
            },
            {
              title: <span style={{ fontSize: '12px' }}>Status</span>,
              key: 'status',
              width: '10%',
              render: (_: any, record: any) => (
                updatingLikelihood === record.id ? (
                  <Tag color="processing" style={{ fontSize: '11px', padding: '2px 6px', margin: 0 }}>Updating</Tag>
                ) : (
                  <Tag color="success" style={{ fontSize: '11px', padding: '2px 6px', margin: 0 }}>Saved</Tag>
                )
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default ScoringMethod;

